import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import {
  Card,
  Button,
  Input,
  Textarea,
  PageHeader,
  Spinner,
  EmptyState,
  Modal,
  Field,
  Select,
  Badge,
  ErrorBanner,
  formatDate,
} from '../components/ui';

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt?: string;
  body?: string;
  status: 'draft' | 'published';
  categoryId?: string;
  publishedAt?: string;
  createdAt: string;
}

interface Category {
  id: string;
  name: string;
  slug: string;
}

export function BlogPage() {
  const [editing, setEditing] = useState<BlogPost | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const qc = useQueryClient();

  const postsQ = useQuery({
    queryKey: ['blog-posts'],
    queryFn: () =>
      api
        .get<{ data: BlogPost[] }>('/pages', { pageType: 'blog_post' })
        .catch(() => ({ data: [] })),
  });

  const catQ = useQuery({
    queryKey: ['blog-categories'],
    queryFn: () => api.get<{ data: Category[] }>('/blog-categories').catch(() => ({ data: [] })),
  });

  const posts = postsQ.data?.data ?? [];
  const categories = catQ.data?.data ?? [];

  return (
    <div>
      <PageHeader
        title="Blog"
        subtitle={`${posts.length} posts`}
        action={<Button onClick={() => setShowCreate(true)}>+ New post</Button>}
      />

      {postsQ.isLoading ? (
        <Spinner />
      ) : posts.length === 0 ? (
        <Card>
          <EmptyState
            title="No posts yet"
            description="Publish articles, news, or guides to attract more visitors."
            action={<Button onClick={() => setShowCreate(true)}>+ New post</Button>}
          />
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {posts.map((p) => (
            <Card key={p.id} onClick={() => setEditing(p)}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{p.title}</div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                    /blog/{p.slug} · {formatDate(p.publishedAt ?? p.createdAt)}
                  </div>
                </div>
                <Badge color={p.status === 'published' ? 'green' : 'gray'}>{p.status}</Badge>
              </div>
            </Card>
          ))}
        </div>
      )}

      {(showCreate || editing) && (
        <PostEditor
          post={editing}
          categories={categories}
          onClose={() => {
            setShowCreate(false);
            setEditing(null);
          }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['blog-posts'] });
            setShowCreate(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function PostEditor({
  post,
  categories,
  onClose,
  onSaved,
}: {
  post: BlogPost | null;
  categories: Category[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(post?.title ?? '');
  const [slug, setSlug] = useState(post?.slug ?? '');
  const [excerpt, setExcerpt] = useState(post?.excerpt ?? '');
  const [body, setBody] = useState(post?.body ?? '');
  const [categoryId, setCategory] = useState(post?.categoryId ?? '');
  const [status, setStatus] = useState<BlogPost['status']>(post?.status ?? 'draft');
  const [error, setError] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: () => {
      const body0 = {
        title,
        slug: slug || title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
        excerpt: excerpt || undefined,
        body,
        categoryId: categoryId || undefined,
        status,
      };
      return post
        ? api.patch(`/pages/${post.id}`, body0)
        : api.post('/pages', { ...body0, pageType: 'blog_post' });
    },
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Save failed'),
  });

  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/pages/${post!.id}`),
    onSuccess: onSaved,
  });

  return (
    <Modal onClose={onClose} title={post ? 'Edit post' : 'New post'} width={780}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!title) return setError('Title required');
          saveMut.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      >
        <Field label="Title *">
          <Input value={title} onChange={setTitle} autoFocus />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-2)' }}>
          <Field label="Slug">
            <Input value={slug} onChange={setSlug} placeholder="auto" />
          </Field>
          <Field label="Category">
            <select value={categoryId} onChange={(e) => setCategory(e.target.value)} style={selectStyle}>
              <option value="">None</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Status">
            <Select
              value={status}
              onChange={setStatus}
              options={[
                { value: 'draft', label: 'Draft' },
                { value: 'published', label: 'Published' },
              ]}
            />
          </Field>
        </div>
        <Field label="Excerpt">
          <Textarea value={excerpt} onChange={setExcerpt} rows={2} />
        </Field>
        <Field label="Body" hint="Markdown supported">
          <Textarea value={body} onChange={setBody} rows={12} />
        </Field>
        <ErrorBanner error={error} />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          {post ? (
            <Button variant="danger" onClick={() => deleteMut.mutate()} disabled={deleteMut.isPending}>
              Delete
            </Button>
          ) : (
            <span />
          )}
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saveMut.isPending}>
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

const selectStyle: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  border: '1px solid var(--color-border-primary)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-sm)',
  background: 'var(--color-surface-primary)',
};
